import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ShieldCheck, KeyRound, CheckCircle2, ArrowRight } from 'lucide-react';
import api from '../api';
import { formatApiErrorDetail } from '../auth';
import { Card, Button, Field, Input, Spinner, Reveal } from '../components/ui';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setToken(params.get('token') || '');
  }, [params]);

  const submit = async (e) => {
    e?.preventDefault();
    setErr('');
    if (!token) { setErr('This reset link is missing its token. Request a new one.'); return; }
    if (password.length < 6) { setErr('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setErr('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
      setTimeout(() => nav('/login', { replace: true }), 1600);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="grain min-h-screen flex items-center justify-center px-5 py-10">
      <Reveal className="w-full max-w-md">
        <div className="flex items-center gap-2.5 justify-center mb-6">
          <div className="w-10 h-10 rounded-xl bg-jade/15 border border-jade/40 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-jade" />
          </div>
          <div className="font-display font-extrabold text-2xl">Tasco</div>
        </div>

        <Card className="p-7" data-testid="reset-card">
          {done ? (
            <div className="text-center py-4" data-testid="reset-success">
              <CheckCircle2 className="w-12 h-12 text-jade mx-auto mb-3" />
              <div className="font-display text-xl font-bold mb-1">Password changed</div>
              <p className="text-slate-400 text-sm">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="w-5 h-5 text-jade" />
                <div className="font-display text-xl font-bold">Choose a new password</div>
              </div>
              <p className="text-slate-400 text-sm mb-5">Enter a new password for your account.</p>
              <form onSubmit={submit} className="space-y-4">
                <Field label="New password">
                  <Input data-testid="reset-password" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                </Field>
                <Field label="Confirm password">
                  <Input data-testid="reset-confirm" type="password" value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" />
                </Field>
                {err && <div className="text-crimson text-sm" data-testid="reset-error">{err}</div>}
                <Button type="submit" data-testid="reset-submit" disabled={loading} className="w-full">
                  {loading ? <Spinner label="…" /> : <>Reset password <ArrowRight className="w-4 h-4" /></>}
                </Button>
              </form>
              <div className="mt-5 text-center">
                <Link to="/login" data-testid="reset-back-login" className="text-sm text-slate-400 hover:text-jade">Back to sign in</Link>
              </div>
            </>
          )}
        </Card>
      </Reveal>
    </div>
  );
}
